"use client";

import { Category, ProductData } from "@/lib/5874/types";
import clsx from "clsx";
import React, { FC, useMemo } from "react";
import styles from "./ProductListItem.module.scss";
import { ProductForm } from "@/components/PDP/ProductForm/ProductForm";
import StockAvailability from "@/components/StockAvailability/StockAvailability";
import { ProductContextProvider } from "@/providers/ProductContextProvider";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import Pricing from "@/components/ProductCard/Pricing/Pricing";
import Link from "@/components/UI/Link/Link";
import useRegionAndLocale from "@/hooks/useRegionAndLocale";
import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import mapProductOptions from "@/hooks/mapProductOptions";
import {
	CertificationMessageSize,
	findProductCertifications,
} from "@/utils/certifications-utils";
import CertificationNotification from "@/components/CertificationNotification/CertificationNotification";
import { ProductClickedTrigger } from "@/lib/_event-bus/components/ProductClickedTrigger";

export interface ProductListItemProps {
	product: ProductData;
	isInCart?: boolean;
	category?: Category;
	currencyCode: string;
}

const ProductListItem: FC<ProductListItemProps> = ({
	product,
	isInCart,
	category,
	currencyCode,
	...props
}) => {
	const { session } = useSessionContext();
	const { region, lang } = useRegionAndLocale();
	const { name, path, brand, sku, id } = product;
	const isCustomer = session !== undefined;

	// Product Certification Restrictions
	const hasCertificationMessage = findProductCertifications(product);

	// Datalayer
	const { selectItem } = useDataLayerContext();

	const productSpecifications = useMemo(() => {
		return mapProductOptions([product]);
	}, [product]);

	return (
		<ProductContextProvider product={product}>
			<ProductClickedTrigger
				product={product}
				eventData={{
					queryId: (props as any)["data-queryid"],
					position: (props as any)["data-position"],
					eventName: (props as any)["data-eventname"],
				}}
			>
				<div className={clsx([styles.item, isInCart ? styles.inCart : null])}>
					<div className={styles.detail}>
						{brand && (
							<Link href={brand?.path} className={styles.brandLink}>
								{brand?.name}
							</Link>
						)}
						{sku && <span className={styles.sku} translate="no">{sku}</span>}
						<Link
							onClick={() => {
								selectItem(
									product,
									id,
									String(category?.id),
									category?.name,
									currencyCode,
								);
							}}
							href={`/${region}/${lang}${path}`}
							className={styles.name}
						>
							{name}
						</Link>
						{productSpecifications && productSpecifications.length > 0 && (
							<div className={styles.specs}>
								{productSpecifications?.map((spec) => (
									<div key={spec.label} className={styles.spec}>
										<span className={styles.label}>{spec.label}:</span>
										<span className={styles.value}>
											{spec.options[0].label}
										</span>
									</div>
								))}
							</div>
						)}
						<span className={styles.price}>
							<Pricing linkClassName={styles.loginLink} />
						</span>
						{hasCertificationMessage && (
							<CertificationNotification
								region={region}
								product={product}
								messageSize={CertificationMessageSize.SMALL}
							/>
						)}
					</div>
					<div className={styles.actions}>
						{isCustomer ? (
							<>
								<ProductForm />
								<StockAvailability product={product} />
							</>
						) : null}
					</div>
				</div>
			</ProductClickedTrigger>
		</ProductContextProvider>
	);
};

export default ProductListItem;
